/**
 * Pricing Block
 * Displays pricing tiers with features, badges, and CTAs.
 * Supports monthly/annual toggle switch for price switching.
 */

import { registerBlockRenderer } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';
import type { PricingData, PricingTier } from '../types';

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidPricingData(data: unknown): data is PricingData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // Must have tiers array
  if (!Array.isArray(d.tiers) || d.tiers.length === 0) return false;

  // Validate each tier has required fields
  for (const tier of d.tiers) {
    if (!tier || typeof tier !== 'object') return false;
    const t = tier as Record<string, unknown>;
    if (typeof t.name !== 'string') return false;
    if (typeof t.price !== 'string') return false;
    if (!Array.isArray(t.features)) return false;
  }

  return true;
}

// =============================================================================
// DATA NORMALIZATION
// =============================================================================

/**
 * Detect dual pricing embedded in period strings and auto-create billingToggle.
 *
 * AI often sends:  price: "£96", period: "/yr (£8/mo)"
 * We normalize to: price: "£8", annualPrice: "£96", period: "/mo" + billingToggle
 */
function normalizePricingData(data: PricingData): PricingData {
  // If toggle already provided, data is already structured
  if (data.billingToggle) return data;

  // Detect monthly price in parentheses: "/yr (£8/mo)" or "/year ($29/month)"
  const monthlyInParens = /\(([£$€¥₹]?\s*[\d,.]+)\s*\/\s*mo(?:nth)?\)/i;
  // Detect annual price in parentheses: "/mo ($348/yr)" or "/month (£96/year)"
  const annualInParens = /\(([£$€¥₹]?\s*[\d,.]+)\s*\/\s*y(?:ea)?r?\)/i;

  const hasMonthlyInParens = data.tiers.some(t => t.period && monthlyInParens.test(t.period));
  const hasAnnualInParens = data.tiers.some(t => t.period && annualInParens.test(t.period));

  if (!hasMonthlyInParens && !hasAnnualInParens) return data;

  const normalizedTiers = data.tiers.map(tier => {
    if (hasMonthlyInParens) {
      // Main price is annual, monthly in parens: "/yr (£8/mo)"
      const match = tier.period?.match(monthlyInParens);
      if (match && match[1]) {
        return {
          ...tier,
          annualPrice: tier.price,        // £96 (original)
          price: match[1].trim(),         // £8 (extracted monthly)
          period: '/mo',
        };
      }
    } else if (hasAnnualInParens) {
      // Main price is monthly, annual in parens: "/mo (£96/yr)"
      const match = tier.period?.match(annualInParens);
      if (match && match[1]) {
        return {
          ...tier,
          annualPrice: match[1].trim(),   // £96 (extracted annual)
          period: '/mo',
        };
      }
    }
    // Tier without dual pricing (e.g., Free) — ensure annualPrice is set
    return { ...tier, annualPrice: tier.annualPrice || tier.price };
  });

  return {
    ...data,
    tiers: normalizedTiers,
    billingToggle: {
      options: [
        { label: 'Monthly', value: 'monthly', selected: true },
        { label: 'Annual', value: 'annual' },
      ],
    },
  };
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function renderPricingTier(tier: PricingTier, isAnnual: boolean, hasBillingToggle: boolean): string {
  const highlighted = tier.highlighted === true;
  const currentPrice = isAnnual && tier.annualPrice ? tier.annualPrice : tier.price;

  let html = `<div class="pms-pricing-tier${highlighted ? ' pms-pricing-tier-highlighted' : ''}">`;

  // Badge (e.g., "Most Popular")
  if (tier.badge) {
    html += `<div class="pms-pricing-badge">${escapeHtml(tier.badge)}</div>`;
  }

  // Header
  html += `<div class="pms-pricing-header">
    <div class="pms-pricing-name">${escapeHtml(tier.name)}</div>
    <div class="pms-pricing-price">
      <span class="pms-pricing-amount" data-monthly="${escapeHtml(tier.price)}" data-annual="${escapeHtml(tier.annualPrice || tier.price)}">${escapeHtml(currentPrice)}</span>
      ${tier.period ? (hasBillingToggle
        ? `<span class="pms-pricing-period" data-monthly-period="/mo" data-annual-period="/yr">${escapeHtml(isAnnual ? '/yr' : '/mo')}</span>`
        : `<span class="pms-pricing-period">${escapeHtml(tier.period)}</span>`
      ) : ''}
    </div>
    ${tier.annualSavings ? `<div class="pms-pricing-annual-savings${isAnnual ? ' pms-pricing-annual-savings-visible' : ''}">${escapeHtml(tier.annualSavings)}</div>` : ''}
    ${tier.description ? `<div class="pms-pricing-description">${escapeHtml(tier.description)}</div>` : ''}
  </div>`;

  // Features — normalize: AI may send strings or {text, included} objects
  html += '<ul class="pms-pricing-features">';
  if (tier.features && tier.features.length > 0) {
    for (const rawFeature of tier.features) {
      // Normalize: string → included feature, object → use as-is
      const feature = typeof rawFeature === 'string'
        ? { text: rawFeature, included: true, tooltip: undefined as string | undefined }
        : rawFeature;

      const icon = feature.included
        ? '<svg viewBox="0 0 24 24" width="16" height="16" class="pms-pricing-check"><path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>'
        : '<svg viewBox="0 0 24 24" width="16" height="16" class="pms-pricing-x"><path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>';

      html += `<li class="pms-pricing-feature${feature.included ? '' : ' pms-pricing-feature-excluded'}">
        ${icon}
        <span>${escapeHtml(feature.text)}</span>
        ${feature.tooltip ? `<span class="pms-pricing-tooltip" title="${escapeHtml(feature.tooltip)}">?</span>` : ''}
      </li>`;
    }
  }
  html += '</ul>';

  // Action button — normalize: AI may send string or {label, value} object
  const action = typeof tier.action === 'string'
    ? { label: tier.action as string, value: tier.action as string, variant: undefined as 'primary' | 'secondary' | undefined }
    : tier.action;
  if (action) {
    const variant = action.variant || (highlighted ? 'primary' : 'secondary');
    html += `<button class="pms-pricing-action pms-pricing-action-${variant}" data-action="${escapeHtml(action.value)}">
      ${escapeHtml(action.label)}
    </button>`;
  }

  html += '</div>';
  return html;
}

function renderBillingToggle(data: PricingData, isAnnual: boolean): string {
  if (!data.billingToggle) return '';

  const options = data.billingToggle.options;
  if (options.length < 2) return '';

  const monthlyLabel = options[0]?.label || 'Monthly';
  const annualLabel = options[1]?.label || 'Annual';

  let html = '<div class="pms-pricing-billing-toggle">';

  // Monthly label
  html += `<span class="pms-pricing-billing-label${!isAnnual ? ' pms-pricing-billing-label-active' : ''}" data-billing="monthly">${escapeHtml(monthlyLabel)}</span>`;

  // Toggle switch
  html += `<button type="button" class="pms-pricing-toggle-switch${isAnnual ? ' pms-pricing-toggle-switch-annual' : ''}" aria-pressed="${isAnnual}" aria-label="Toggle between monthly and annual billing">
    <span class="pms-pricing-toggle-knob"></span>
  </button>`;

  // Annual label
  html += `<span class="pms-pricing-billing-label${isAnnual ? ' pms-pricing-billing-label-active' : ''}" data-billing="annual">${escapeHtml(annualLabel)}</span>`;

  // Savings badge
  if (data.billingToggle.savings) {
    html += `<span class="pms-pricing-savings-badge">${escapeHtml(data.billingToggle.savings)}</span>`;
  }

  html += '</div>';
  return html;
}

// =============================================================================
// RENDER FUNCTION
// =============================================================================

function renderPricing(
  rawData: PricingData,
  container: HTMLElement,
  onAction?: (value: string) => void
): void {
  // Normalize: detect dual pricing in period strings, auto-create toggle
  const data = normalizePricingData(rawData);
  const hasBillingToggle = !!data.billingToggle;

  const wrapper = createBlockWrapper('pricing');

  // Determine initial billing state from toggle options
  let isAnnual = false;
  if (data.billingToggle && data.billingToggle.options.length >= 2) {
    isAnnual = data.billingToggle.options[1]?.selected === true;
  }

  let html = '';

  // Optional title
  if (data.title) {
    html += `<h3 class="pms-pricing-title">${escapeHtml(data.title)}</h3>`;
  }

  // Billing toggle (monthly/annual switch)
  html += renderBillingToggle(data, isAnnual);

  // Pricing tiers
  html += '<div class="pms-pricing-tiers">';
  for (const tier of data.tiers) {
    html += renderPricingTier(tier, isAnnual, hasBillingToggle);
  }
  html += '</div>';

  wrapper.innerHTML = html;

  // Add action handlers
  if (onAction) {
    wrapper.querySelectorAll('[data-action]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const action = (el as HTMLElement).dataset.action;
        if (action) {
          onAction(action);
        }
      });
    });
  }

  // Billing toggle functionality
  const toggleSwitch = wrapper.querySelector('.pms-pricing-toggle-switch');
  const labels = wrapper.querySelectorAll('.pms-pricing-billing-label');

  const updatePrices = (annual: boolean) => {
    // Update toggle switch state
    toggleSwitch?.classList.toggle('pms-pricing-toggle-switch-annual', annual);
    toggleSwitch?.setAttribute('aria-pressed', String(annual));

    // Update labels
    labels.forEach(label => {
      const billing = (label as HTMLElement).dataset.billing;
      label.classList.toggle('pms-pricing-billing-label-active',
        (billing === 'annual' && annual) || (billing === 'monthly' && !annual)
      );
    });

    // Update prices
    wrapper.querySelectorAll('.pms-pricing-amount[data-monthly]').forEach(el => {
      const priceEl = el as HTMLElement;
      const price = annual ? priceEl.dataset.annual : priceEl.dataset.monthly;
      if (price) {
        priceEl.textContent = price;
      }
    });

    // Update periods (e.g., /mo ↔ /yr)
    wrapper.querySelectorAll('.pms-pricing-period[data-monthly-period]').forEach(el => {
      const periodEl = el as HTMLElement;
      periodEl.textContent = annual
        ? (periodEl.dataset.annualPeriod || '')
        : (periodEl.dataset.monthlyPeriod || '');
    });

    // Show/hide annual savings
    wrapper.querySelectorAll('.pms-pricing-annual-savings').forEach(el => {
      el.classList.toggle('pms-pricing-annual-savings-visible', annual);
    });
  };

  // Toggle switch click handler
  toggleSwitch?.addEventListener('click', () => {
    isAnnual = !isAnnual;
    updatePrices(isAnnual);
  });

  // Label click handlers (allow clicking labels to toggle)
  labels.forEach(label => {
    label.addEventListener('click', () => {
      const billing = (label as HTMLElement).dataset.billing;
      const newAnnual = billing === 'annual';
      if (newAnnual !== isAnnual) {
        isAnnual = newAnnual;
        updatePrices(isAnnual);
      }
    });
  });

  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('pricing', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  if (!isValidPricingData(data)) {
    console.warn('[pricing] Invalid data:', data);
    const wrapper = createBlockWrapper('pricing');
    wrapper.innerHTML = '<div class="pms-block-error">Invalid pricing data</div>';
    container.appendChild(wrapper);
    return;
  }

  renderPricing(data, container, onAction);
});
